import _ from 'lodash';
import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import {
  applyThreshold,
  applyThresholdColor,
  getThresholdString,
  toFrontendDuration,
} from '@/tools/thresholdMetric/thresholdMetric.utilities';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams } from '@/tools/tool.utilities';
import { getStatisticFromFragment } from '@/utilities/statistic.utilities';
import { ProcessTypeEnum } from '@seeqdev/api/model/ThresholdMetricOutputV1';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { BackendDuration } from '@/services/systemConfiguration.types';
import type { StoredStatistic } from '@/tools/StatisticSelector.molecule';
import type { DurationPayload } from '@/tools/thresholdMetric/thresholdMetric.utilities';
import type {
  BaseToolPersistedState,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';
import type { ThresholdOutputV1 } from '@seeqdev/api/model/ThresholdOutputV1';

export type StoredThresholds = Record<number, { threshold?: string; color?: string }>;

export const parameterDefinitions: ParameterDefinitions = {
  measuredItem: { predicate: ['name', 'measuredItem'] },
  boundingCondition: { predicate: ['name', 'boundingCondition'] },
};

const parameterDefinitionKeys = Object.keys(parameterDefinitions);

interface ThresholdMetricEditPayload extends ToolRehydratePayload {
  aggregationFunction?: string;
  processType?: ProcessTypeEnum;
  duration?: unknown;
  period?: unknown;
  boundingConditionMaximumDuration?: unknown;
  thresholds?: ThresholdOutputV1[];
}

export interface ThresholdMetricStoreState extends BaseToolPersistedState {
  measuredItem?: ToolItem;
  boundingCondition?: ToolItem;
  processType: ProcessTypeEnum;
  aggregationOperator: StoredStatistic;
  duration: Partial<DurationPayload>;
  period: Partial<DurationPayload>;
  boundingConditionMaximumDuration: Partial<DurationPayload> | undefined;
  thresholds: StoredThresholds;
  neutralColor: string | undefined;
}

export interface ThresholdMetricStoreActions {
  setSearchName: (name: string) => void;
  setProcessType: (processType: ProcessTypeEnum | undefined) => void;
  setAggregationOperator: (aggregationOperator: StoredStatistic) => void;
  setDuration: (duration: DurationPayload) => void;
  setPeriod: (period: DurationPayload) => void;
  setBoundingConditionMaximumDuration: (value: number, units: string) => void;
  setMeasuredItem: (item: ToolItem | null | undefined) => void;
  setBoundingCondition: (item: ToolItem | null | undefined) => void;
  /** Sets the transition value for one of the thresholds, or removes it when `threshold` is `undefined`. */
  setThreshold: (level: number, threshold: string | undefined) => void;
  /** Sets a custom color for one of the thresholds. Level 0 is the special neutral (non-threshold) case. */
  setCustomThresholdColor: (level: number, color: string) => void;
  /** `defaultMaxCapsuleDuration` is passed in by the caller (rather than read from `systemConfiguration.utilities`
   * here) so this store only ever operates on its own state. May be undefined if system config hasn't loaded yet. */
  rehydrateForEdit: (
    payload: ThresholdMetricEditPayload,
    defaultMaxCapsuleDuration: DurationPayload | undefined,
  ) => void;
  reset: ResettableStoreState<ThresholdMetricStoreState>;
}

export type ThresholdMetricDehydratedState = ThresholdMetricStoreState;

// A factory (rather than a single shared object) so every caller gets its own fresh nested objects
// (aggregationOperator/thresholds/etc). Immer auto-freezes state after each produce() call, so reusing the same
// nested object reference across multiple `set` calls (e.g. in rehydrateForEdit's reset-to-initial) would hand
// back an already-frozen object that a later mutation on the draft would throw trying to write to. Declaring the
// return type here (rather than inline in Object.freeze's argument) also means every property is checked against
// ThresholdMetricStoreState directly, without needing per-property `as` casts to widen literals like `null`.
const createInitialState = (): ThresholdMetricStoreState => ({
  ...BASE_TOOL_COMMON_PROPS,
  measuredItem: undefined,
  boundingCondition: undefined,
  processType: ProcessTypeEnum.Simple,
  aggregationOperator: {
    key: null,
    timeUnits: 's',
    percentile: null,
    multiplier: null,
  },
  duration: {},
  period: {},
  boundingConditionMaximumDuration: undefined,
  thresholds: {},
  neutralColor: undefined,
});

const initialState: ThresholdMetricStoreState = Object.freeze(createInitialState());

export const dehydrateThresholdMetricStoreState = (state: ThresholdMetricStoreState): ThresholdMetricDehydratedState =>
  state;

const thresholdMetricActionsFactory: ActionsFactory<ThresholdMetricStoreState, ThresholdMetricStoreActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setProcessType: (processType) =>
    set((draft) => {
      if (processType !== undefined) {
        draft.processType = processType;
      }
    }),

  setAggregationOperator: (aggregationOperator) =>
    set((draft) => {
      draft.aggregationOperator = aggregationOperator;
    }),

  setDuration: (duration) =>
    set((draft) => {
      draft.duration = duration;
    }),

  setPeriod: (period) =>
    set((draft) => {
      draft.period = period;
    }),

  setBoundingConditionMaximumDuration: (value, units) =>
    set((draft) => {
      draft.boundingConditionMaximumDuration = { value, units };
    }),

  setMeasuredItem: (item) =>
    set((draft) => {
      draft.measuredItem = item ?? undefined;
    }),

  setBoundingCondition: (item) =>
    set((draft) => {
      draft.boundingCondition = item ?? undefined;
    }),

  setThreshold: (level, threshold) =>
    set((draft) => {
      applyThreshold(draft, level, threshold);
    }),

  setCustomThresholdColor: (level, color) =>
    set((draft) => {
      applyThresholdColor(draft, level, color);
    }),

  rehydrateForEdit: (payload, defaultMaxCapsuleDuration) => {
    set((draft) => {
      // Reset to initial state directly (rather than calling resetState(), which would trigger its own
      // subscription notification) so rehydration is a single atomic update. Uses a fresh createInitialState()
      // rather than the shared frozen `initialState` — see the comment on createInitialState for why.
      Object.assign(draft, createInitialState());

      const rehydrationState = buildToolRehydrationState({ payload, parameterDefinitions });
      // aggregationOperator/duration/period/boundingConditionMaximumDuration/thresholds are populated below with
      // custom parsing, so the raw merge must not overwrite them with the unconverted payload values.
      const config = _.omit(rehydrationState.config, [
        'aggregationOperator',
        'duration',
        'period',
        'boundingConditionMaximumDuration',
        'thresholds',
      ]);

      Object.assign(draft, config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
      });

      const operator = getStatisticFromFragment(payload.aggregationFunction ?? '');
      if (operator) {
        draft.aggregationOperator = {
          key: operator.key,
          timeUnits: operator.timeUnits ?? draft.aggregationOperator.timeUnits,
          percentile: operator.percentile ?? draft.aggregationOperator.percentile,
          multiplier: operator.multiplier ?? draft.aggregationOperator.multiplier,
        };
      }

      draft.duration = toFrontendDuration(payload.duration as BackendDuration | undefined) ?? {};
      draft.period = toFrontendDuration(payload.period as BackendDuration | undefined) ?? {};
      draft.boundingConditionMaximumDuration =
        toFrontendDuration(payload.boundingConditionMaximumDuration as BackendDuration | undefined) ??
        defaultMaxCapsuleDuration;

      if (payload.processType !== undefined) {
        draft.processType = payload.processType;
      }

      _.forEach(payload.thresholds ?? [], (threshold) => {
        if (!threshold.priority) {
          return;
        }

        applyThreshold(draft, threshold.priority.level, getThresholdString(threshold));

        if (threshold.priority.color) {
          applyThresholdColor(draft, threshold.priority.level, threshold.priority.color);
        }
      });
    });
  },

  reset: (overrides) => resetState(overrides),
});

const STORE_NAME = 'ThresholdMetric';

const [ThresholdMetricStoreProvider, useThresholdMetricStoreInstance, useThresholdMetricStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  thresholdMetricActionsFactory,
);

export { ThresholdMetricStoreProvider };

/** The persisted UIConfig for this tool. Only `type` and `advancedParametersCollapsed` are kept; the rest of the
 * tool's state is stored via its own parameters/formula rather than the config blob. */
const thresholdMetricConfigParamsSelector = createSelector(
  [(state: ThresholdMetricStoreState) => state],
  (state): ToolConfigParams => {
    const config = getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.THRESHOLD_METRIC,
      parameterDefinitionKeys,
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    });
    return { type: config.type, advancedParametersCollapsed: config.advancedParametersCollapsed };
  },
);

export const useThresholdMetricId = () => useThresholdMetricStoreSelector((state) => state.id);
export const useThresholdMetricName = () => useThresholdMetricStoreSelector((state) => state.name);
export const useThresholdMetricOriginalParameters = () =>
  useThresholdMetricStoreSelector((state) => state.originalParameters);
export const useThresholdMetricConfigVersion = () => useThresholdMetricStoreSelector((state) => state.configVersion);
export const useThresholdMetricMeasuredItem = () => useThresholdMetricStoreSelector((state) => state.measuredItem);
export const useThresholdMetricBoundingCondition = () =>
  useThresholdMetricStoreSelector((state) => state.boundingCondition);
export const useThresholdMetricProcessType = () => useThresholdMetricStoreSelector((state) => state.processType);
export const useThresholdMetricAggregationOperator = () =>
  useThresholdMetricStoreSelector((state) => state.aggregationOperator);
export const useThresholdMetricDuration = () => useThresholdMetricStoreSelector((state) => state.duration);
export const useThresholdMetricPeriod = () => useThresholdMetricStoreSelector((state) => state.period);
/** Raw store value, undefined until explicitly set. Callers that need a display value should fall back to
 * `getDefaultMaxCapsuleDuration()` themselves — the system config default isn't part of this store's own state. */
export const useThresholdMetricBoundingConditionMaximumDuration = () =>
  useThresholdMetricStoreSelector((state) => state.boundingConditionMaximumDuration);
export const useThresholdMetricThresholds = () => useThresholdMetricStoreSelector((state) => state.thresholds);
export const useThresholdMetricNeutralColor = () => useThresholdMetricStoreSelector((state) => state.neutralColor);
export const useThresholdMetricConfigParams = () =>
  useThresholdMetricStoreSelector(thresholdMetricConfigParamsSelector);

export const useThresholdMetricActions = () => useThresholdMetricStoreInstance().actions;

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. Prefer
 * `<TestStoreProvider>` or `<*StoreProvider>` with UI-driven assertions instead.
 */
export function createTestThresholdMetricStore(overrides?: Partial<ThresholdMetricStoreState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestThresholdMetricStore() should only be used in tests.');
  }
  return createStoreInstance({ ...initialState, ...overrides }, thresholdMetricActionsFactory);
}

/**
 * @deprecated Legacy singleton bridge for non-React consumers (e.g. `investigate.actions.ts`'s edit-rehydration
 * dispatch) that cannot use hooks. New code should use the hooks above and the Provider.
 */
export function legacyCreateThresholdMetricStore() {
  return createStoreInstance<ThresholdMetricStoreState, ThresholdMetricStoreActions>(
    initialState,
    thresholdMetricActionsFactory,
    { storeName: STORE_NAME },
  );
}

export const usePersistThresholdMetricStore = () => {
  const register = useRegisterPersistedStore();
  const store = useThresholdMetricStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqThresholdMetricStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateThresholdMetricStoreState,
    });
  }, [register, store]);
};
